import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  Dumbbell, Shield, Heart, Zap, Target, Brain, 
  ArrowRight, ChevronLeft, Sparkles, Activity,
  TrendingUp, Users, Lock, Repeat
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import PublicNavbar from "./PublicNavbar";
import AthleticLifespanChart from "./AthleticLifespanChart";

const PhilosophyPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const sections = [
    {
      id: "systems",
      title: "We Build Systems, Not Workouts",
      icon: Target,
      content: `Most training programs are collections of exercises. Ours is an integrated developmental system.

Each component prepares the body for the next:

Health → Capacity → Strength → Power → Elastic Expression → Performance

Nothing is random. Nothing is isolated. Nothing is included simply because it is popular.

Every element exists to make the others more effective.`
    },
    {
      id: "muscle",
      title: "Athletic Muscle That \"Shows and Goes\"",
      icon: Dumbbell,
      content: `Muscle can be built in many ways. Not all of them improve performance.

Some training produces size without speed, mass without mobility, or strength that does not transfer to movement. We pursue a different outcome:

Athletic muscle that looks powerful — and performs powerfully.

We build muscle that both shows and goes:`,
      bullets: [
        "Visibly dense, developed, and athletic",
        "Capable of producing high levels of usable force",
        "Efficient across full ranges of motion",
        "Supported by strong connective tissues",
        "Metabolically prepared for repeated effort",
        "Optimized for strength-to-weight ratio"
      ],
      footer: `This is not bodybuilding for appearance alone, nor minimalist training that ignores muscular development. It is structural hypertrophy designed to enhance performance.

When muscle is trained correctly, aesthetics follow function — and performance confirms it.

If it doesn't transfer, it doesn't qualify.`
    },
    {
      id: "nea",
      title: "Neuro-Elastic Amplification",
      icon: Zap,
      content: `Elite speed and explosiveness come from more than muscle. The highest performers leverage elastic tissues as biological energy-recycling systems.

We develop Neuro-Elastic Amplification (NEA) — the coordinated ability of the nervous system and connective tissues to store and release energy rapidly.

This produces:`,
      bullets: [
        "Faster sprinting with lower energy cost",
        "Higher reactive power output",
        "Shorter ground contact times",
        "Superior change-of-direction ability",
        "Greater durability under repeated impacts"
      ],
      footer: "Muscle generates force. Elastic tissues multiply efficiency."
    },
    {
      id: "series",
      title: "Series Strength: The Strength That Moves",
      icon: Activity,
      content: `Raw strength is valuable only if it transfers through the body efficiently.

We train series strength — the ability of the muscle-tendon unit to transmit force without dissipation. This complements traditional contractile strength and enables fluid, explosive movement.

Balanced development includes:

Parallel Strength — force production capacity
Series Strength — force transmission and elastic return

Together, they produce athletes who are not only strong, but fast, reactive, and economical.`
    },
    {
      id: "individualization",
      title: "Individualization Within Structure",
      icon: Users,
      content: `No two athletes adapt identically. Genetics, training history, injury profile, sport demands, and recovery capacity all influence outcomes.

However, effective personalization requires a stable framework.

We adjust variables within a proven system — not by abandoning structure entirely.

Personalized does not mean improvised.`
    },
    {
      id: "preparedness",
      title: "Preparedness Reduces Injury",
      icon: Shield,
      content: `Injury typically occurs when applied forces exceed tissue capacity.

We increase capacity so athletes can tolerate the realities of competition through:`,
      bullets: [
        "Progressive loading",
        "Eccentric strength development",
        "Deceleration training",
        "Tissue conditioning",
        "Controlled return-to-performance pathways"
      ],
      footer: "Avoidance creates fragility. Preparation creates durability."
    },
    {
      id: "discipline",
      title: "Discipline Over Motivation",
      icon: Lock,
      content: `Motivation fluctuates. Systems produce results.

We emphasize consistent execution, intelligent progression, and long-term development over emotional intensity or short bursts of effort.`
    },
    {
      id: "stewardship",
      title: "Stewardship, Not Transaction",
      icon: Heart,
      content: `The name "Paladin" reflects a duty to protect and develop those entrusted to us.

Coaching is not entertainment. It is applied responsibility for human performance and well-being.

We do not promise shortcuts.
We do not rely on gimmicks.
We do not sacrifice long-term health for short-term results.

We build capable humans.`
    },
    {
      id: "freedom",
      title: "The Objective: Freedom Through Capability",
      icon: Sparkles,
      content: `High health enables high performance. High performance expands opportunity.

Strength provides options.
Speed creates advantage.
Elastic power improves efficiency.
Resilience sustains progress over decades.

Our mission is direct:`,
      footer: "To develop individuals who are powerful, efficient, durable, and prepared for the physical demands of sport, work, and life."
    }
  ];

  return (
    <div className="min-h-screen bg-[#030711] text-white">
      {/* Navigation */}
      <PublicNavbar />

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-block text-amber-400 text-sm font-semibold tracking-[0.3em] uppercase mb-6">
              The Paladin Philosophy
            </span>
            <h1 className="text-5xl md:text-7xl lg:text-8xl font-black tracking-tighter mb-8 leading-[0.9]">
              THE PALADIN<br/>
              <span className="bg-gradient-to-r from-blue-400 via-blue-500 to-amber-400 bg-clip-text text-transparent">
                DOCTRINE
              </span>
            </h1>
            <p className="text-2xl md:text-3xl font-light text-gray-300 mb-8">
              Health First. Performance Always.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Opening Statement */}
      <section className="py-16 px-6 border-t border-white/5">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="space-y-6 text-lg text-gray-300 leading-relaxed"
          >
            <p>
              At Paladin Health & Human Performance, we operate from a simple but non-negotiable principle:
            </p>
            <blockquote className="text-2xl md:text-3xl font-bold text-white border-l-4 border-amber-400 pl-6 my-8">
              Health is the foundation upon which all performance is built.
            </blockquote>
            <p>
              Without structural integrity, nervous system readiness, metabolic stability, and recovery capacity, high performance cannot be sustained. Talent cannot compensate for fragility. Effort cannot overcome dysfunction indefinitely.
            </p>
            <p className="text-white font-medium">
              We do not chase peak outputs at the expense of long-term capability.
            </p>
            <p>
              We build systems that allow high performance to be expressed repeatedly, safely, and progressively over time.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Athletic Lifespan Graph */}
      <section className="py-20 px-6 bg-[#070B14]">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="text-center mb-12">
              <span className="inline-block text-amber-400 text-sm font-semibold tracking-[0.3em] uppercase mb-4">
                The Athletic Lifespan
              </span>
              <h2 className="text-3xl md:text-5xl font-black tracking-tight mb-4">
                CAPABILITY OVER A LIFETIME
              </h2>
              <p className="text-lg text-gray-300 max-w-2xl mx-auto">
                Physical qualities don't have to fall off a cliff. With the right system, the decline is slow, controlled, and dignified — Olympic-level athletes prove it's possible at every age.
              </p>
            </div>

            <AthleticLifespanChart />

            {/* Key takeaways */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-10">
              <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                <p className="text-3xl font-black text-blue-400 mb-2">8 → Prime</p>
                <p className="text-gray-300 text-sm">Physical capabilities rise rapidly through development. The system accelerates this growth through structured, age-appropriate programming.</p>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                <p className="text-3xl font-black text-amber-400 mb-2">30 → 60+</p>
                <p className="text-gray-300 text-sm">The goal isn't to stop aging — it's to control the slope. Masters Olympic athletes maintain 65-88% of peak capacity decades past their prime.</p>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                <p className="text-3xl font-black text-emerald-400 mb-2">The Gap</p>
                <p className="text-gray-300 text-sm">The difference between trained and untrained widens every year. At 60, a trained individual can outperform an untrained 30-year-old.</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Main Content Sections */}
      {sections.map((section, index) => (
        <section 
          key={section.id}
          className={`py-20 px-6 ${index % 2 === 0 ? 'bg-[#070B14]' : 'bg-[#030711]'}`}
        >
          <div className="max-w-3xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              {/* Section Header */}
              <div className="flex items-start gap-4 mb-8">
                <div className="w-14 h-14 bg-gradient-to-br from-blue-600/20 to-amber-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                  <section.icon className="w-7 h-7 text-blue-400" />
                </div>
                <h2 className="text-3xl md:text-4xl font-black tracking-tight leading-tight">
                  {section.title}
                </h2>
              </div>

              {/* Section Content */}
              <div className="space-y-6 text-lg text-gray-300 leading-relaxed pl-0 md:pl-[4.5rem]">
                {section.content.split('\n\n').map((paragraph, pIndex) => (
                  <p key={pIndex} className={paragraph.includes('→') ? 'text-white font-medium text-xl' : ''}>
                    {paragraph}
                  </p>
                ))}

                {/* Bullets if present */}
                {section.bullets && (
                  <ul className="space-y-3 my-8">
                    {section.bullets.map((bullet, bIndex) => (
                      <li key={bIndex} className="flex items-start gap-3">
                        <div className="w-2 h-2 bg-amber-400 rounded-full mt-2.5 flex-shrink-0" />
                        <span>{bullet}</span>
                      </li>
                    ))}
                  </ul>
                )}

                {/* Footer if present */}
                {section.footer && (
                  <div className="pt-4 border-t border-white/10 mt-8">
                    {section.footer.split('\n\n').map((paragraph, fIndex) => (
                      <p key={fIndex} className={`${fIndex > 0 ? 'mt-4' : ''} ${section.id === 'freedom' ? 'text-xl text-white font-bold' : 'text-white/80 italic'}`}>
                        {paragraph}
                      </p>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        </section>
      ))}

      {/* Divider Line */}
      <div className="max-w-3xl mx-auto px-6">
        <div className="h-px bg-gradient-to-r from-transparent via-amber-400/50 to-transparent" />
      </div>

      {/* CTA Section */}
      <section className="py-24 px-6 bg-[#030711]">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-black tracking-tight mb-6">
              READY TO BEGIN?
            </h2>
            <p className="text-xl text-gray-400 mb-10 max-w-2xl mx-auto">
              Join a system built on principles, not trends. Train with purpose. Build for the long term.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => navigate("/pricing")}
                className="group bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-500 hover:to-blue-600 text-white px-10 py-4 rounded-full font-bold text-lg tracking-wide uppercase transition-all flex items-center justify-center gap-2"
                data-testid="philosophy-cta-pricing"
              >
                View Programs
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
              <button
                onClick={() => navigate("/")}
                className="border border-white/20 text-white px-10 py-4 rounded-full font-semibold hover:bg-white/5 transition-colors"
              >
                Back to Home
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-white/5 bg-[#030711]">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-3">
              <Dumbbell className="w-6 h-6 text-blue-500" />
              <span className="font-bold tracking-tight">PALADIN</span>
              <span className="text-gray-500 text-sm">Health & Human Performance</span>
            </div>
            <p className="text-gray-500 text-sm">
              HEALTH FIRST | PERFORMANCE ALWAYS
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PhilosophyPage;
